import { ReactNode, useEffect } from 'react'
import { TabBar, AppToast } from './TabBar'
import { SideNav } from './SideNav'

export interface LayoutProps {
  children: ReactNode
  header?: ReactNode
  showTabBar?: boolean
  className?: string
  /** When true, skip the inner scroll wrapper and render children
   * directly as the flex child of the main column. Use this for
   * surfaces that own their own scroll region (e.g. a chat detail
   * page where the composer must stay pinned to the viewport bottom
   * and the message thread scrolls independently). */
  bareBody?: boolean
}

export function Layout({
  children,
  header,
  showTabBar = true,
  className = '',
  bareBody = false,
}: LayoutProps) {
  // The `dark` class is owned by `settingsStore.applyTheme()` and
  // lives on `<html>`. Don't duplicate it here — having two sources
  // of truth caused the theme switch to lag behind state on remounts.
  useEffect(() => {
    document.body.classList.add('scroll-lock')
    return () => document.body.classList.remove('scroll-lock')
  }, [])

  return (
    <div className="transition-colors">
      <div className="flex h-screen h-[100dvh] overflow-hidden">
        {/* Desktop side nav — always visible on md+ */}
        <SideNav />

        {/* Main content area */}
        <div className={`flex-1 flex flex-col h-full min-w-0 ${className}`}>
          {/* Header */}
          {header && (
            <div
              className="sticky top-0 z-10 header-blur border-b border-gray-100/60 md:border-b-0 dark:border-gray-800/60"
              style={{ paddingTop: 'var(--safe-area-inset-top)' }}
            >
              <div className="w-full px-0 md:px-2">
                {header}
              </div>
            </div>
          )}

          {/* Scrollable content. `bareBody` opts out of the inner
              scroll wrapper so children can own a full-height flex
              frame (used by chat detail to pin the composer). */}
          {bareBody ? (
            <div className="flex-1 min-h-0 flex flex-col">{children}</div>
          ) : (
            <div className="flex-1 overflow-y-auto app-scroll">
              <div className={`w-full ${showTabBar ? 'pb-32 md:pb-6' : ''}`}>
                {children}
              </div>
            </div>
          )}

          {/* Mobile floating tab bar + toast */}
          {showTabBar && (
            <>
              <TabBar />
              <AppToast />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
